import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { Network, type ConnectionStatus } from '@capacitor/network';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { CapacitorWifi } from '@capgo/capacitor-wifi';

export interface ConnectivityStatus {
  connected: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  // state signals
  private readonly _status = signal<ConnectivityStatus>({ connected: true });
  private readonly _initialized = signal(false);
  private readonly log = createLogger('NetworkService');

  // Observable stream derived from signal for RxJS subscribers
  readonly status$ = toObservable(this._status);

  readonly status = this._status.asReadonly();
  readonly initialized = this._initialized.asReadonly();

  // computed
  get isConnected(): boolean {
    return this._status().connected;
  }

  get isOffline(): boolean {
    return !this._status().connected;
  }

  /**
   * Initialize network monitoring.
   * Call once at app startup (e.g., in APP_INITIALIZER).
   */
  async init$(): Promise<void> {
    if (this._initialized()) {
      return; // Prevent double initialization
    }

    try {
      await this.initNetwork$();
      this._initialized.set(true);
    } catch (error) {
      this.log.error('Initialization failed', { error });
    }
  }

  /**
   * Initialize network monitoring
   */
  private async initNetwork$(): Promise<void> {
    const initialStatus = await Network.getStatus();
    this.updateStatus(initialStatus.connected);

    await Network.addListener('networkStatusChange', (newStatus: ConnectionStatus) => {
      this.updateStatus(newStatus.connected);
    });
  }

  private updateStatus(connected: boolean): void {
    this._status.set({ connected });
  }

  // Manual status check (useful for one-off queries)
  async checkStatus$(): Promise<ConnectivityStatus> {
    try {
      const status = await Network.getStatus();
      this.updateStatus(status.connected);
    } catch (error) {
      this.log.error('checkStatus$ failed', { error });
    }
    return this._status();
  }

  /**
   * Whether the phone's WiFi radio is on — stricter than `isConnected`, which is true for
   * cellular too. On web there is no radio API, so the tracked online status (navigator.onLine)
   * is the closest proxy — false when the machine's WiFi is off. A failing radio check must
   * never hard-block a flow — the actual connect attempt surfaces a proper error anyway.
   */
  async checkWifiEnabled$(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return this._status().connected;
    }
    try {
      const { enabled } = await CapacitorWifi.isEnabled();
      return enabled;
    } catch (error) {
      console.error('[NetworkService] checkWifiEnabled failed:', error);
      return true;
    }
  }
}
